/** Project workspace: overview, documents, material, financials, activity. */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Tabs } from "@primereact/ui/tabs";
import { type ProjectCapability } from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { apiDelete, apiGet } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { ProjectMaterial } from "../catalog/movements.js";
import { StatusTimeline } from "../commercial/document.js";
import {
  ConfirmButton,
  Detail,
  DetailGrid,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
  formatDate,
} from "../shared.js";
import type { ProjectDetail } from "./types.js";
import { ProjectFinances } from "./ProjectFinances.js";
import {
  ProjectActivity,
  ProjectDocuments,
  ProjectTeam,
  StatusChange,
} from "./ProjectPanels.js";

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { id = "" } = useParams();
  const client = useQueryClient();
  const [tab, setTab] = useState("overview");
  const query = useQuery({
    queryKey: ["project", id],
    queryFn: () =>
      apiGet<{ data: ProjectDetail }>(`/projects/${id}`).then(
        (response) => response.data,
      ),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/projects/${id}`),
    onSuccess: () => window.location.assign("/projects"),
  });
  if (query.isLoading) return <LoadingPanel />;
  if (query.error || !query.data) return <ErrorPanel error={query.error} />;
  const project = query.data;
  const can = (capability: ProjectCapability) =>
    project.effectiveCapabilities.includes(capability);
  const customerName =
    project.customer.companyName ||
    `${project.customer.firstName} ${project.customer.lastName}`.trim();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["project", id] }),
      client.invalidateQueries({ queryKey: ["projects"] }),
      client.invalidateQueries({ queryKey: ["history", "project", id] }),
    ]);
  const hoursRatio = project.plannedMinutes
    ? project.actualMinutes / project.plannedMinutes
    : 0;
  return (
    <>
      <PageHeader
        title={project.title}
        subtitle={
          <>
            {project.number} ·{" "}
            <Link
              className="text-brand hover:underline"
              to={`/customers/${project.customer.id}`}
            >
              {customerName}
            </Link>
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={project.status} />
            {can("edit") ? (
              <>
                <StatusChange project={project} onDone={refresh} />
                <Button
                  as={Link}
                  to={`/projects/${id}/edit`}
                  variant="outlined"
                  severity="secondary"
                  icon="pi pi-pencil"
                >
                  {t("common.edit")}
                </Button>
              </>
            ) : null}
          </div>
        }
      />
      <Tabs.Root
        value={tab}
        onValueChange={(event: { value: unknown }) =>
          setTab(String(event.value))
        }
      >
        <Tabs.List className="overflow-x-auto">
          <Tabs.Tab value="overview">{t("projects.overview")}</Tabs.Tab>
          <Tabs.Tab value="documents">{t("projects.documents")}</Tabs.Tab>
          {permissions.has("inventory.view") ? (
            <Tabs.Tab value="material">{t("projects.material")}</Tabs.Tab>
          ) : null}
          {can("view_costs") ? (
            <Tabs.Tab value="financials">{t("projects.financials")}</Tabs.Tab>
          ) : null}
          {permissions.has("settings.system.edit") ? (
            <Tabs.Tab value="activity">{t("projects.activity")}</Tabs.Tab>
          ) : null}
        </Tabs.List>
        <Tabs.Panels>
          <Tabs.Panel value="overview">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
              <div className="space-y-4">
                <Section title={t("projects.details")}>
                  <p className="whitespace-pre-wrap text-sm">
                    {project.description || (
                      <span className="text-app-muted">
                        {t("projects.noDescription")}
                      </span>
                    )}
                  </p>
                  {project.internalNotes ? (
                    <div className="mt-4 rounded-lg bg-app-background p-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-app-muted">
                        {t("projects.internalNotes")}
                      </h3>
                      <p className="mt-1 whitespace-pre-wrap text-sm">
                        {project.internalNotes}
                      </p>
                    </div>
                  ) : null}
                </Section>
                {project.children.length ? (
                  <Section title={t("projects.children")}>
                    <ul className="divide-y divide-app-border">
                      {project.children.map((child) => (
                        <li
                          key={child.id}
                          className="flex items-center justify-between py-2.5"
                        >
                          <Link
                            className="font-medium text-brand hover:underline"
                            to={`/projects/${child.id}`}
                          >
                            {child.title}
                            <span className="ml-2 text-xs font-normal text-app-muted">
                              {child.number}
                            </span>
                          </Link>
                          <StatusBadge status={child.status} />
                        </li>
                      ))}
                    </ul>
                  </Section>
                ) : null}
                <StatusTimeline
                  entityType="project"
                  id={project.id}
                  version={project.version}
                />
              </div>
              <div className="space-y-4">
                <Section title={t("projects.summary")}>
                  <DetailGrid>
                    <Detail
                      label={t("common.priority")}
                      value={t(`priority.${project.priority}`, {
                        defaultValue: project.priority,
                      })}
                    />
                    <Detail
                      label={t("projects.schedule")}
                      value={
                        project.plannedStart || project.plannedEnd
                          ? `${formatDate(project.plannedStart) || "…"} – ${formatDate(project.plannedEnd) || "…"}`
                          : "—"
                      }
                    />
                    <Detail
                      label={t("projects.hours")}
                      value={
                        <span
                          className={hoursRatio > 1 ? "text-danger" : undefined}
                        >
                          {(project.actualMinutes / 60).toFixed(1)}h /{" "}
                          {(project.plannedMinutes / 60).toFixed(1)}h
                        </span>
                      }
                    />
                    <Detail
                      label={t("common.currency")}
                      value={project.currency}
                    />
                  </DetailGrid>
                </Section>
                <ProjectTeam
                  project={project}
                  canManage={can("manage_members")}
                />
                {project.status === "planned" &&
                permissions.has("projects.delete") ? (
                  <div className="flex flex-col gap-2">
                    <ConfirmButton
                      variant="danger"
                      onConfirm={() => remove.mutate()}
                      confirmMessage={t("projects.deleteConfirm")}
                      loading={remove.isPending}
                    >
                      {t("projects.delete")}
                    </ConfirmButton>
                    <MutationError error={remove.error} />
                  </div>
                ) : null}
              </div>
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="documents">
            <ProjectDocuments id={id} />
          </Tabs.Panel>
          <Tabs.Panel value="material">
            {permissions.has("inventory.view") ? (
              <Section title={t("projects.material")}>
                <ProjectMaterial
                  projectId={id}
                  canManage={
                    can("manage_inventory") &&
                    project.status !== "completed" &&
                    project.status !== "cancelled"
                  }
                />
              </Section>
            ) : null}
          </Tabs.Panel>
          <Tabs.Panel value="financials">
            {can("view_costs") ? (
              <ProjectFinances id={id} canManage={can("manage_costs")} />
            ) : null}
          </Tabs.Panel>
          <Tabs.Panel value="activity">
            {permissions.has("settings.system.edit") ? (
              <ProjectActivity id={id} />
            ) : null}
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs.Root>
    </>
  );
}
